/**
 * @fileOverview
 * All variables that get passed in to the mainIssueAggQuery are defined here.
 * The abstraction is necessary because there are multiple call paths to mainIssueAggQuery,
 * so having a single source of truth for the variables eliminates a vector for
 * bugs and inconsistencies when they are invoked.
 *
 * This file will be removed (and therefore the need for the linting exemption) once we reduce
 * the number of discrete call paths for mainIssueAggQuery to one. That work will be done in
 * https://jira.atlassian.com/browse/JIV-19486.
 *
 * Sorry, Tom.
 *
 * <3 Daz
 */
/* eslint-disable jira/ff/inline-usage */
import { LICENSE_STATE_ACTIVE } from '@jira/platform__license-state/src/license-state.tsx';
import { FREE_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { JIRA_SOFTWARE } from '@jira/platform__application-editions/src/application.tsx';
import { SOFTWARE } from '@jira/platform__application-editions/src/application-key.tsx';
import { isProjectsInJiraM0Enabled } from '@jira/platform__services-native-integration-projects/src/index.tsx';
import { getPermalinkIdFromURL, COMMENTS } from '@jira/issue__permalink/src/getPermalinkId.tsx';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import {
	CUSTOM_FIELD_MODULE,
	CUSTOM_FIELD_TYPE_MODULE,
	ISSUE_ACTION_MODULE,
	ISSUE_ACTIVITY_MODULE,
	ISSUE_CONTEXT_MODULE,
	ISSUE_GLANCE_MODULE,
	ISSUE_PANEL_MODULE,
	ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
	UI_MODIFICATIONS_MODULE,
} from '@jira/forge__forge-ui-constants/src/constants.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import getEditions from '@atlassian/jira-common-util-get-tenant-context/src/edition/index.tsx';
import getLicenseStates from '@atlassian/jira-common-util-get-tenant-context/src/license-state/index.tsx';
import {
	getIsAnonymous,
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';

import mainIssueAggQuery from './__generated__/mainIssueAggQuery$parameters.ts';

type ProvidedVar = (typeof mainIssueAggQuery)['params']['providedVariables'];
type ProvidedVariables = { [k in keyof ProvidedVar]: unknown };

// Cloned from @jira/issue-table__issue-table/src/common/constants.tsx
const MAX_AMOUNT_OF_COLUMNS = 500;
const IS_ISSUE_VIEW_CONTEXT_PANEL_FIELDS_REORDERING_ENABLED = false;

const hasActiveJiraSoftwareLicense = (): boolean =>
	getLicensedProducts(getMeta)[LICENSED_PRODUCTS.JIRA_SOFTWARE] === true &&
	getLicenseStates(getMeta).software === LICENSE_STATE_ACTIVE;

const isTwcEntitlementCheckEnabled = (): boolean =>
	hasActiveJiraSoftwareLicense() && fg('twcg-1399_twc_project_kickoff_workflow_killswitch');

// Gates a backend-sensitive TWC co-use entitlement lookup that requires an active Jira Software
// license, so the fetch does not fire on non-JSW issue views (targeting already restricts the CTA
// to supported Jira Software project/issue types).
const isTwcCoUseRovoCheckEnabled = (): boolean =>
	hasActiveJiraSoftwareLicense() && fg('twcg_1846_twc_couse_jira_pt_rovo_create_killswitch');

const getMainIssueViewAggProvidedVariables = (): ProvidedVariables => {
	const providerVariables: Record<keyof NonNullable<ProvidedVar>, { get: () => unknown }> =
		mainIssueAggQuery?.params?.providedVariables ?? {};
	return Object.entries(providerVariables).reduce(
		(acc: Record<string, unknown>, [key, provider]) => {
			acc[key] = provider.get();
			return acc;
		},
		{},
	);
};

export const getRovoDevPanelVariables = (): {
	/**
	 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
	 * from the main Rovo Dev in Jira panel in the issue view.
	 *
	 * Fragment: RovodevAgentPanelWithEntitlementCheck
	 * Component: RovodevAgentPanelWithEntitlementCheck
	 */
	issueViewRovoDevAgentsPanelEntitlementEnabled: boolean;
	/**
	 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
	 * for the Agents panel/assign to rovo flow in the issue view.
	 *
	 * Fragment: IssueViewAgentPanel_rovodevQuery
	 * Component: IssueViewAgentPanel
	 */
	isAssignToRovoDevActive: boolean;
} => {
	const isAnonymous = getIsAnonymous(getMeta);

	return {
		/**
		 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
		 * from the main Rovo Dev in Jira panel in the issue view.
		 *
		 * Fragment: RovodevAgentPanelWithEntitlementCheck
		 * Component: RovodevAgentPanelWithEntitlementCheck
		 */
		issueViewRovoDevAgentsPanelEntitlementEnabled:
			// eslint-disable-next-line jira/ff/no-preconditioning
			!fg('devai_issue_view_query_separation') &&
			(fg('rovodev-in-jira-panel-is-anonymous-precondition') ? !isAnonymous : true) &&
			fg('boysenberry_rovodev_main_release') &&
			fg('boysenberry_enable_entitlements_check'),
		/**
		 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
		 * for the Agents panel/assign to rovo flow in the issue view.
		 *
		 * Fragment: IssueViewAgentPanel_rovodevQuery
		 * Component: IssueViewAgentPanel
		 */
		isAssignToRovoDevActive:
			// eslint-disable-next-line jira/ff/no-preconditioning
			!fg('devai_issue_view_query_separation') &&
			(fg('rovodev-in-jira-panel-is-anonymous-precondition') ? !isAnonymous : true) &&
			fg('boysenberry_rovodev_main_release') &&
			fg('devai_assign_to_rovodev_main_release') &&
			fg('boysenberry_enable_entitlements_check'),
	};
};

export const getMainIssueViewAggQueryFeatureFlagVariables = (): {
	issueViewSlackSyncInfoEnabled: boolean;
	issueViewGoalsFieldWithShowAllGoalsFlag: boolean;
	relayMigrationWebLinksFlag: boolean;
	relayMigrationIssueFieldAttachments: boolean;
	skipAttachmentsQuery: boolean;
	relayMigrationChildrenIssuesSectionEnabled: boolean;
	isWorkItemModernizationEnabled: boolean;
	relayMigrationArchival: boolean;
	issueViewAtlasProjectFieldEnabled: boolean;
	isAiAgentSessionsPanelEnabled: boolean;
	removeIssueViewInteractivequeryM4: boolean;
	frontierFormulaFieldD1Flag: boolean;
	frontierFormulaFieldD1TextFlag: boolean;
	frontierFormulaFieldD1DateFlag: boolean;
	prefetchChildIssuesPanelQuery: boolean; // When child work SSR FG is enabled do not prefetch child issues panel query
	relayMigrationCommentCount: boolean;
	shouldFetchAppType: boolean;
	childWorkItemsSSRUIEnabled: boolean;
	isAttachmentsConsolidationEnabled: boolean;
	isRcaV2UiEnabled: boolean;
	isJourneyExecutionsPanelEnabled: boolean;
	isAssignToRovoServiceButtonMainEnabled: boolean;
	isJsmIncidentCommandCenterEnabled: boolean;
	isModernisedActivityFeedEnabled: boolean;
	isGuidedAutonomyEapEnabled: boolean;
	isCsmAgentWorkspaceEnabled: boolean;
	isCsmAgentWorkspaceActionsEnabled: boolean;
	commentsSortBy: {
		field: 'CREATED';
		order: 'DESC';
	};
	/**
	 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
	 * from the main Rovo Dev in Jira panel in the issue view.
	 *
	 * Fragment: RovodevAgentPanelWithEntitlementCheck
	 * Component: RovodevAgentPanelWithEntitlementCheck
	 */
	issueViewRovoDevAgentsPanelEntitlementEnabled: boolean;
	/**
	 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
	 * for the Agents panel/assign to rovo flow in the issue view.
	 *
	 * Fragment: IssueViewAgentPanel_rovodevQuery
	 * Component: IssueViewAgentPanel
	 */
	isAssignToRovoDevActive: boolean;

	customFieldConfigurationFlag: boolean;

	isProactiveIssueAISummaryEnabled: boolean;

	issueViewUserPreferredLayoutFieldEnabled: boolean;
	issueViewDetailsPanelFieldsReorderingEnabled: boolean;
	issueViewContextPanelFieldsReorderingEnabled: boolean;
	issueViewCoverExperimentEnabled: boolean;
	includeCappedCommentDataForIssueViewedEvent: boolean;
	isAutomationDiscoverabilityEnabled: boolean;
	nikeDfoEcosystemBackgroundScriptsMigration: boolean;
	isSmartRepliesV2Enabled: boolean;
	isTwcEntitlementCheckEnabled: boolean;
	isTwcCoUseRovoCheckEnabled: boolean;
	isJsmRequestFormsInRequestDetailsEnabled: boolean;
	shouldSkipCmdbSearchUrl: boolean;
	isChatOpsEntitlementFromAggEnabled: boolean;
	mentionedConfluencePageLinksV2PaginationFlag: boolean;
	skipAgentSessionsForAnonymous: boolean;
} => ({
	customFieldConfigurationFlag: fg('issue_view_field_config_edit'),
	isProactiveIssueAISummaryEnabled: fg('proactive_ai_generated_work_item_summary_m1_gate'),
	issueViewUserPreferredLayoutFieldEnabled: fg('issue_view_layout_experiment_feature_gate'),
	issueViewDetailsPanelFieldsReorderingEnabled:
		expValEquals(
			'jira-work-item-reordering-fields-v2',
			'reorderingBehaviour',
			'withDragHandler',
			'noReordering',
		) ||
		expValEquals(
			'jira-work-item-reordering-fields-v2',
			'reorderingBehaviour',
			'withoutDragHandler',
			'noReordering',
		),
	// Keep the query shape for the later relay cleanup while forcing this path off in all cohorts.
	issueViewContextPanelFieldsReorderingEnabled:
		IS_ISSUE_VIEW_CONTEXT_PANEL_FIELDS_REORDERING_ENABLED,
	issueViewCoverExperimentEnabled:
		fg('cover_image_issue_view_experiment_kill_switch') || fg('cover_image_issue_view_ga'),
	includeCappedCommentDataForIssueViewedEvent: fg('thor_add_metadata_for_experiment_learning'),
	isAutomationDiscoverabilityEnabled: fg('automation_discoverability_in_jsm'),
	nikeDfoEcosystemBackgroundScriptsMigration: !fg('nike-dfo-ecosystem-bg-scripts-migration-router'),
	...getRovoDevPanelVariables(),
	mentionedConfluencePageLinksV2PaginationFlag: fg('jiv_confluence_remote_link_critical_query'),
	issueViewSlackSyncInfoEnabled: fg('jira_issue_view_show_slack_sync_info'),
	issueViewGoalsFieldWithShowAllGoalsFlag:
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jira_preview_panel_m_1_rollout') && fg('jira-glance-panel'),
	relayMigrationWebLinksFlag: fg('relay-migration-web-links-fetch'),
	relayMigrationIssueFieldAttachments:
		fg('relay_migration_issue_field_attachments') &&
		!expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false),
	skipAttachmentsQuery:
		fg('relay_migration_issue_field_attachments') ||
		expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false),
	relayMigrationChildrenIssuesSectionEnabled: fg('relay_migration_children_issues_section'),
	isWorkItemModernizationEnabled:
		// eslint-disable-next-line jira/ff/no-work-item-modernization-exp-val
		expVal('work_item_modernization', 'isEnabled', false) ||
		fg('enable_work_item_for_quadratic_release'),
	relayMigrationArchival: fg('relay-migration-archival'),
	issueViewAtlasProjectFieldEnabled: isProjectsInJiraM0Enabled(),
	isAiAgentSessionsPanelEnabled: fg('jira_assignable_agent_session_experience'),
	removeIssueViewInteractivequeryM4: fg('remove-issueviewinteractivequery-m4'),
	frontierFormulaFieldD1Flag: fg('frontier-formula-field-d1'),
	frontierFormulaFieldD1TextFlag: fg('frontier-formula-field-d2-d3'),
	frontierFormulaFieldD1DateFlag: fg('frontier-formula-field-d2-d3'),
	prefetchChildIssuesPanelQuery: !fg('jira-issue-view-child-work-items-ssr-fg'), // When child work SSR FG is enabled do not prefetch child issues panel query
	relayMigrationCommentCount: fg('relay-migration-comment-count'),
	shouldFetchAppType: fg('jira_ai_agent_avatar_tmp_board'),
	childWorkItemsSSRUIEnabled: fg('jira-issue-view-child-work-items-ssr-fg'),
	isAttachmentsConsolidationEnabled: expVal(
		'attachments-1p-consolidation-experiment',
		'isExperimentEnabled',
		false,
	),
	isRcaV2UiEnabled: fg('aiops_rca_v2_ui'),
	isJourneyExecutionsPanelEnabled: fg('start-3737-journey-panel'),
	isAssignToRovoServiceButtonMainEnabled: fg('separate_component_for_assign_rovo_service_button'),
	isJsmIncidentCommandCenterEnabled: fg('jsm_incident_command_center'),
	isModernisedActivityFeedEnabled: fg('jsm_aiwi_modernised_activity_feed'),
	isGuidedAutonomyEapEnabled: expVal('frontier-guided-autonomy-eap', 'isEnabled', false),
	isCsmAgentWorkspaceEnabled: fg('csm-omni-channel-live-chat-in-widget'),
	isCsmAgentWorkspaceActionsEnabled: fg('csm-omni-channel-live-chat-in-widget'),
	isSmartRepliesV2Enabled: expVal(
		'jira-smart-replies-v2-main-agg-query-change-sprt',
		'isEnabled',
		false,
	),
	// Gates a backend-sensitive TWC entitlement lookup that requires an active Jira Software license.
	isTwcEntitlementCheckEnabled: !getIsAnonymous(getMeta) && isTwcEntitlementCheckEnabled(),
	// Enable the 1846-specific Rovo signal query only for the active experiment cohort.
	isTwcCoUseRovoCheckEnabled: !getIsAnonymous(getMeta) && isTwcCoUseRovoCheckEnabled(),
	commentsSortBy: { field: 'CREATED' as const, order: 'DESC' as const },
	isJsmRequestFormsInRequestDetailsEnabled: fg('jsm_request_forms_in_request_details'),
	shouldSkipCmdbSearchUrl: fg('flash-839-remove-searchurl-from-cmdb-field'),
	isChatOpsEntitlementFromAggEnabled: fg('jsm_main_issue_agg_chatops_entitlement'),
	skipAgentSessionsForAnonymous: fg('jira-ai-read-agent-sessions') && getIsAnonymous(getMeta),
});

// JIV-19906: retrieve query string from URL -- only expected to be used by client side code
// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const getURLFromWindow = () => new URL(window.location.href);

// JIV-19906: retrieve query string from url
// server-side: expect url to be passed
// client-side: read from window
// WARNING: ONly a subset of meta tags are available here due to early script execution on ssr failure.
// Please make sure to add any meta tags being used to jira/src/entry-fs/utils/get-pre-html.tsx
export const getMainIssueViewAggQueryVariables = (
	issueKey: string | null,
	url: URL = getURLFromWindow(),
): {
	applicationKey: 'JIRA_SOFTWARE';
	issueViewAddAttachmentFlag: boolean;
	isInTimelineOrCalendarView: boolean;
	shouldUseNewChildWorkItemsDatafetcher: boolean;
	forgeTypes: (
		| 'jira:issueAction'
		| 'jira:issueGlance'
		| 'jira:issueContext'
		| 'jira:issuePanel'
		| 'jira:issueActivity'
		| 'jira:customField'
		| 'jira:customFieldType'
		| 'jira:issueViewBackgroundScript'
		| 'jira:uiModifications'
	)[];
	issueSearchInput:
		| {
				jql: string;
				childIssuesInput?: undefined;
		  }
		| {
				childIssuesInput: {
					parentIssueKey: string;
				};
				jql?: undefined;
		  };
	fieldSetsInput: {
		viewInput: {
			namespace: string;
			context: {
				issueContext: {
					issueKey: string | null;
				};
			};
		};
	};
	childIssuesFieldIds: string[];
	childIssuesAmountOfColumns: number;
	childIssuesFirst: null;
	isInitialLoadQuery: boolean;
	issueViewStorageLimitsBannerFlag: boolean;
	productKey: 'jira-software';
	changeOfferingOfferingName: string;
	commentTargetId: string | null;
	hasCommentTargetId: boolean;
	issueViewSlackSyncInfoEnabled: boolean;
	issueViewGoalsFieldWithShowAllGoalsFlag: boolean;
	relayMigrationWebLinksFlag: boolean;
	relayMigrationIssueFieldAttachments: boolean;
	skipAttachmentsQuery: boolean;
	relayMigrationChildrenIssuesSectionEnabled: boolean;
	isWorkItemModernizationEnabled: boolean;
	relayMigrationArchival: boolean;
	issueViewAtlasProjectFieldEnabled: boolean;
	isAiAgentSessionsPanelEnabled: boolean;
	removeIssueViewInteractivequeryM4: boolean;
	frontierFormulaFieldD1Flag: boolean;
	frontierFormulaFieldD1TextFlag: boolean;
	frontierFormulaFieldD1DateFlag: boolean;
	prefetchChildIssuesPanelQuery: boolean; // When child work SSR FG is enabled do not prefetch child issues panel query
	relayMigrationCommentCount: boolean;
	shouldFetchAppType: boolean;
	childWorkItemsSSRUIEnabled: boolean;
	isAttachmentsConsolidationEnabled: boolean;
	isRcaV2UiEnabled: boolean;
	isJourneyExecutionsPanelEnabled: boolean;
	isAssignToRovoServiceButtonMainEnabled: boolean;
	isJsmIncidentCommandCenterEnabled: boolean;
	isModernisedActivityFeedEnabled: boolean;
	isGuidedAutonomyEapEnabled: boolean;
	isCsmAgentWorkspaceEnabled: boolean;
	isCsmAgentWorkspaceActionsEnabled: boolean;
	commentsSortBy: {
		field: 'CREATED';
		order: 'DESC';
	};
	mentionedConfluencePageLinksV2PaginationFlag: boolean;
	/**
	 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
	 * from the main Rovo Dev in Jira panel in the issue view.
	 *
	 * Fragment: RovodevAgentPanelWithEntitlementCheck
	 * Component: RovodevAgentPanelWithEntitlementCheck
	 */
	issueViewRovoDevAgentsPanelEntitlementEnabled: boolean;
	/**
	 * Enable the devai_rovodevIssueViewQuery fragment for fetching Rovo Dev entitlements
	 * for the Agents panel/assign to rovo flow in the issue view.
	 *
	 * Fragment: IssueViewAgentPanel_rovodevQuery
	 * Component: IssueViewAgentPanel
	 */
	isAssignToRovoDevActive: boolean;

	customFieldConfigurationFlag: boolean;

	isProactiveIssueAISummaryEnabled: boolean;

	issueViewUserPreferredLayoutFieldEnabled: boolean;
	issueViewDetailsPanelFieldsReorderingEnabled: boolean;
	issueViewContextPanelFieldsReorderingEnabled: boolean;
	issueViewCoverExperimentEnabled: boolean;
	includeCappedCommentDataForIssueViewedEvent: boolean;
	isAutomationDiscoverabilityEnabled: boolean;
	nikeDfoEcosystemBackgroundScriptsMigration: boolean;
	isSmartRepliesV2Enabled: boolean;
	isTwcEntitlementCheckEnabled: boolean;
	isTwcCoUseRovoCheckEnabled: boolean;
	isJsmRequestFormsInRequestDetailsEnabled: boolean;
	shouldSkipCmdbSearchUrl: boolean;
	isChatOpsEntitlementFromAggEnabled: boolean;
	skipAgentSessionsForAnonymous: boolean;
	// This value must be an insanely large number, otherwise we'll get some serious bugs! Remove once 'jpx-000-shadow-relay-query' experiment is completed.
	limitFields: number;
} => ({
	// This value must be an insanely large number, otherwise we'll get some serious bugs! Remove once 'jpx-000-shadow-relay-query' experiment is completed.
	limitFields: 99999,
	...getMainIssueViewAggProvidedVariables(),
	...getMainIssueViewAggQueryFeatureFlagVariables(),
	...getCommentQueriesFromURL(url),
	...getStorageBannerVariables(),
	...getIssueSearchForChildIssueQueryVariables(issueKey),
	...getForgeSpecificVariables(),
	...getIsFromSoftwareCalendarOrTimelineViewQueryVariables(),
	...getStorageHardLimitVariables(),
});

const getIsFromSoftwareCalendarOrTimelineViewQueryVariables = () => {
	if (typeof window !== 'undefined') {
		const pathname = window.location.pathname;
		const pattern = /^\/jira\/software(?:\/c)?\/projects\/[^/]+\/boards\/\d+\/(calendar|timeline)$/;
		return { isInTimelineOrCalendarView: pattern.test(pathname) };
	}
	return { isInTimelineOrCalendarView: false };
};

// Exported for testing
export const getChildIssuesFieldIdsQueryVariables = (): string[] => {
	/**
	 * Default fields we're fetching in child issues:
	 * - issuetype: Used to separate base-level and subtasks in epics (see `jira-issue-view-child-issues-panel-merge-subtasks`)
	 */
	const childIssuesFieldIds = ['issuetype'];

	// If we're not fetching the "Include subtasks" timetracking data through Relay
	// include it in `mainIssueAggQuery` so it can be derived from Redux
	if (!fg('relay-migration-edit-worklog') && !fg('jpx-93-unship-legacy-timetracking'))
		childIssuesFieldIds.push('timetracking');

	// If we're not fetching the progress summary status data through Relay,
	// include it in `mainIssueAggQuery` so it can be derived from Redux
	if (!fg('jira-issue-view-child-issues-panel-realtime-update')) childIssuesFieldIds.push('status');

	return childIssuesFieldIds;
};

const getIssueSearchForChildIssueQueryVariables = (issueKey: string | null) => ({
	issueSearchInput: fg('jira-issue-view-child-work-items-ssr-fg')
		? { jql: issueKey ? `parent = ${issueKey}` : '' }
		: { childIssuesInput: { parentIssueKey: issueKey || '' } },
	fieldSetsInput: {
		viewInput: { namespace: 'CHILD_ISSUE_PANEL', context: { issueContext: { issueKey } } },
	},
	childIssuesFieldIds: getChildIssuesFieldIdsQueryVariables(),
	childIssuesAmountOfColumns: MAX_AMOUNT_OF_COLUMNS,
	childIssuesFirst: null,
	isInitialLoadQuery: true,
	shouldUseNewChildWorkItemsDatafetcher: fg('jira_child_issues_new_datafetcher'),
});

export const getCommentQueriesFromURL = (
	url: URL,
): {
	commentTargetId: string | null;
	hasCommentTargetId: boolean;
} => {
	const commentTargetId = getPermalinkIdFromURL(COMMENTS, url);

	return { commentTargetId, hasCommentTargetId: !!commentTargetId };
};

const getStorageBannerVariables = () => ({
	issueViewStorageLimitsBannerFlag: getEditions(getMeta).software === FREE_EDITION,
	productKey: SOFTWARE,
	changeOfferingOfferingName: 'standard',
});

/**
 *
 * @param EXCLUDE_MODULES
 * CAUTION : filter can be expensive, so memoize this value if excluding any modules OR create a singleton
 * When changing forgeTypes here, update workItemForgeTypes in @jira/work-item/src/workItemForgeTypes.ts
 */
export const getForgeSpecificVariables = (
	EXCLUDE_MODULES?: string[],
): {
	forgeTypes: (
		| 'jira:issueAction'
		| 'jira:issueGlance'
		| 'jira:issueContext'
		| 'jira:issuePanel'
		| 'jira:issueActivity'
		| 'jira:customField'
		| 'jira:customFieldType'
		| 'jira:issueViewBackgroundScript'
		| 'jira:uiModifications'
	)[];
} => {
	let allModules = [
		ISSUE_ACTION_MODULE,
		ISSUE_GLANCE_MODULE,
		ISSUE_CONTEXT_MODULE,
		ISSUE_PANEL_MODULE,
		ISSUE_ACTIVITY_MODULE,
		CUSTOM_FIELD_MODULE,
		CUSTOM_FIELD_TYPE_MODULE,
		ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
		UI_MODIFICATIONS_MODULE,
	];
	if (EXCLUDE_MODULES) {
		allModules = allModules.filter((module) => EXCLUDE_MODULES.includes(module));
	}
	return {
		forgeTypes: allModules,
	};
};

const getStorageHardLimitVariables = () => {
	/* ======================================================================================== */
	/* To fetch attachmentStorageUsed from JiraQuery, using applicationKey: JIRA_SOFTWARE is appropriate. */
	/* The applicationKey is an enum that specifies the application context, and JIRA_SOFTWARE is commonly */
	/* used for Jira Software-related queries. */
	/* ======================================================================================== */

	return {
		applicationKey: JIRA_SOFTWARE,
		issueViewAddAttachmentFlag:
			getEditions(getMeta).software === FREE_EDITION &&
			getLicenseStates(getMeta).software === LICENSE_STATE_ACTIVE,
	};
};
